// import { useState, useCallback } from "react";
// import { getHomeworks, deleteHomework } from "../../../services/HomeworkService";
// import { enqueueSnackbar } from "notistack";

// const INITIAL_FILTERS = {
//   program_id: "",
//   subject_id: "",
//   homework_type: "",
//   homework_title: "",
// };

// export const useHomeworks = () => {
//   const [homeworkList, setHomeworkList] = useState([]);

//   // 🔹 Draft filters (UI only)
//   const [filters, setFilters] = useState(INITIAL_FILTERS);

//   // 🔹 Applied filters (API only)
//   const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

//   const [isHomeworkLoading, setIsHomeworkLoading] = useState(false);
//   const [lastPage, setLastPage] = useState(1);
//   const [currentPage, setCurrentPage] = useState(0);

//   /* ================= FETCH ================= */
//   const fetchHomeworks = useCallback(
//     async (page = 1, filterPayload = appliedFilters) => {
//       setIsHomeworkLoading(true);
//       try {
//         const response = await getHomeworks({
//           ...filterPayload,
//           page,
//         });

//         if (response.status === 200) {
//           const data = response.data.data;
//           setHomeworkList(data.data || []);
//           setLastPage(data.last_page || 1);
//           setCurrentPage(page - 1);
//         }
//       } catch (error) {
//         enqueueSnackbar("Failed to fetch homeworks", { variant: "error" });
//       } finally {
//         setIsHomeworkLoading(false);
//       }
//     },
//     [appliedFilters]
//   );

//   /* ================= SEARCH ================= */
//   const handleSearch = useCallback(() => {
//     setAppliedFilters(filters);      // ✅ apply filters
//     fetchHomeworks(1, filters);      // ✅ API call only here
//   }, [filters, fetchHomeworks]);

//   /* ================= RESET ================= */
//   const handleReset = useCallback(() => {
//     setFilters(INITIAL_FILTERS);
//     setAppliedFilters(INITIAL_FILTERS);
//     fetchHomeworks(1, INITIAL_FILTERS);
//   }, [fetchHomeworks]);

//   /* ================= FILTER CHANGE (NO API) ================= */
//   const handleFilterChange = useCallback((e) => {
//     const { name, value } = e.target;

//     setFilters((prev) => ({
//       ...prev,
//       [name]: value,
//       ...(name === "program_id" && { subject_id: "" }), // reset subject
//     }));
//   }, []);

//   /* ================= PAGINATION ================= */
//   const handlePageChange = useCallback(
//     ({ selected }) => {
//       fetchHomeworks(selected + 1);
//     },
//     [fetchHomeworks]
//   );

//   /* ================= DELETE ================= */
//   const deleteHomeworkById = useCallback(
//     async (id) => {
//       try {
//         const response = await deleteHomework(id);
//         if (response.status === 200) {
//           enqueueSnackbar(response.data.message.success, {
//             variant: "success",
//           });

//           fetchHomeworks(currentPage + 1);
//         }
//       } catch {
//         enqueueSnackbar("Failed to delete homework", { variant: "error" });
//       }
//     },
//     [fetchHomeworks, currentPage]
//   );

//   return {
//     homeworkList,
//     filters, // 👈 UI uses this
//     isHomeworkLoading,
//     lastPage,
//     currentPage,

//     handleFilterChange,
//     handleSearch,
//     handleReset,
//     handlePageChange,
//     deleteHomeworkById,
//     fetchHomeworks,
//   };
// };


import { useState, useCallback, useRef } from "react";
import { getHomeworks, deleteHomework } from "../../../services/HomeworkService";
import { enqueueSnackbar } from "notistack";

const INITIAL_FILTERS = {
  program_id: "",
  subject_id: "",
  homework_type: "",
  homework_title: "",
};

export const useHomeworks = () => {
  const [homeworkList, setHomeworkList] = useState([]);

  // UI filters (typing/selecting only)
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  // Applied filters (API only)
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  const [isHomeworkLoading, setIsHomeworkLoading] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);

  // 🔒 Prevent parallel API calls
  const isFetchingRef = useRef(false);

  /* ================= FETCH ================= */
  const fetchHomeworks = useCallback(async (page = 1, payload) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsHomeworkLoading(true);

    try {
      const response = await getHomeworks({
        ...(payload || appliedFilters),
        page,
      });

      if (response.status === 200) {
        const data = response.data.data;
        setHomeworkList(data.data || []);
        setLastPage(data.last_page || 1);
        setCurrentPage(page - 1);
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch homeworks", { variant: "error" });
    } finally {
      isFetchingRef.current = false;
      setIsHomeworkLoading(false);
    }
  }, [appliedFilters]);

  /* ================= SEARCH ================= */
  const handleSearch = useCallback(() => {
    setAppliedFilters(filters);
    fetchHomeworks(1, filters);
  }, [filters, fetchHomeworks]);

  /* ================= RESET ================= */
  const handleReset = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    fetchHomeworks(1, INITIAL_FILTERS);
  }, [fetchHomeworks]);

  /* ================= FILTER CHANGE (NO API) ================= */
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "program_id" && { subject_id: "" }),
    }));
  }, []);

  /* ================= PAGINATION ================= */
  const handlePageChange = useCallback(
    ({ selected }) => {
      fetchHomeworks(selected + 1);
    },
    [fetchHomeworks]
  );

  /* ================= DELETE ================= */
  const deleteHomeworkById = useCallback(
    async (id) => {
      try {
        const response = await deleteHomework(id);
        if (response.status === 200) {
          enqueueSnackbar(response.data.message.success, {
            variant: "success",
          });

          fetchHomeworks(currentPage + 1);
        }
      } catch {
        enqueueSnackbar("Failed to delete homework", { variant: "error" });
      }
    },
    [fetchHomeworks, currentPage]
  );

  return {
    homeworkList,
    filters,
    isHomeworkLoading,
    lastPage,
    currentPage,

    handleFilterChange,
    handleSearch,
    handleReset,
    handlePageChange,
    deleteHomeworkById,
    fetchHomeworks,
  };
};
